import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "& fieldset": { borderColor: "#9D9D9D", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#9D9D9D" },
    "&.Mui-focused": { backgroundColor: "#FFFFFF" },
    "&.Mui-focused fieldset": { borderColor: "#4E93E1", borderWidth: "2px" },
  },
  "& .MuiInputBase-input": { padding: "12px 14px", fontSize: "16px", fontWeight: 400 },
};

const submitButtonStyle = {
  height: 48,
  borderRadius: "8px",
  backgroundColor: "#4E93E1",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  boxShadow: "none",
  color: "#fff",
  "&:hover": { backgroundColor: "#2563EB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#4E93E1", opacity: 0.6, color: "#fff" },
};

/**
 * OtpModal
 *
 * Presentational component — all state and handlers come from useOtpVerification.
 *
 * Props mirror the hook's return values so you can spread them directly:
 *   <OtpModal mobile={watchedMobile} {...otpHook} />
 *
 * @param {string}   mobile           - Raw 10-digit mobile (used to call verifyOtp / resendOtp)
 * @param {boolean}  open             - Controls dialog visibility (= showOtpModal from hook)
 * @param {Function} onClose          - Closes the modal (= closeModal from hook)
 * @param {string}   otp              - Controlled OTP value
 * @param {Function} setOtp           - OTP setter
 * @param {string}   [otpError]       - Error message shown below the OTP field
 * @param {boolean}  isVerifyPending  - Disables Verify button while in-flight
 * @param {boolean}  isResendPending  - Shows "Resending…" state
 * @param {boolean}  canResend        - Whether resend is unlocked
 * @param {number}   countdown        - Seconds remaining before resend is allowed
 * @param {Function} verifyOtp        - Called with (mobile) on Verify click
 * @param {Function} resendOtp        - Called with (mobile) on Resend click
 */
const OtpModal = ({
  mobile,
  open,
  onClose,
  otp,
  setOtp,
  otpError,
  isVerifyPending,
  isResendPending,
  canResend,
  countdown,
  verifyOtp,
  resendOtp,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: { borderRadius: "16px", width: "100%", maxWidth: 420, p: 1 },
    }}
  >
    <DialogContent>
      {/* Header */}
      <Box textAlign="center" mb={3}>
        <Typography variant="h5" fontWeight={700} color="text.primary" fontSize="20px">
          Verify OTP!!
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          mt={1}
          fontSize="14px"
          fontWeight={400}
        >
          Please enter the OTP sent to your mobile
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        {/* OTP Input */}
        <Box>
          <Typography
            variant="body2"
            fontWeight={400}
            mb={1}
            color="text.primary"
            fontSize="14px"
          >
            Enter OTP
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter your OTP"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            autoFocus
            error={!!otpError}
            helperText={otpError}
            sx={textFieldStyle}
            InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
          />
        </Box>

        {/* Resend row */}
        <Box textAlign="right">
          <Typography
            variant="body2"
            component="span"
            color="text.secondary"
            fontSize="14px"
            fontWeight={400}
          >
            Resend OTP:{" "}
          </Typography>

          {canResend ? (
            <Typography
              variant="body2"
              component="span"
              color="#4E93E1"
              fontWeight={500}
              fontSize="14px"
              onClick={() => resendOtp(mobile)}
              sx={{
                cursor: isResendPending ? "default" : "pointer",
                opacity: isResendPending ? 0.6 : 1,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {isResendPending ? "Resending…" : "Resend"}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              component="span"
              color="#4E93E1"
              fontSize="14px"
              fontWeight={400}
            >
              0:{countdown.toString().padStart(2, "0")} sec
            </Typography>
          )}
        </Box>

        {/* Verify button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => verifyOtp(mobile)}
          disabled={isVerifyPending || !otp || otp.length < 4}
          sx={submitButtonStyle}
        >
          {isVerifyPending ? "Verifying…" : "Verify"}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

export default OtpModal;